import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { membershipApplicationAPI } from '../../services/api';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import Button from '../../components/UI/Button';
import { Search, AlertCircle, CheckCircle, PhoneCall } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import HelpTooltip from '../../components/UI/HelpTooltip';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.background.primary};
`;

const Content = styled.div`
  flex: 1;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  padding: 2rem;
`;

const StatusCard = styled(Card)`
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
`;

const Description = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const StatusSummary = styled.div`
  margin-top: 2rem;
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: 12px;
  padding: 1.5rem;
  background: ${props => props.theme.colors.background.secondary};
`;

const SummaryRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 1rem;
`;

const SummaryLabel = styled.span`
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.text.secondary};
`;

const SummaryValue = styled.span`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 2rem;
`;

const TimelineStep = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 10px;
  background-color: ${({ theme, state }) => {
    switch (state) {
      case 'completed':
        return theme.colors.success + '15';
      case 'current':
        return theme.colors.primary + '15';
      default:
        return theme.colors.background.primary;
    }
  }};
  border: 1px solid ${({ theme, state }) => {
    switch (state) {
      case 'completed':
        return theme.colors.success + '40';
      case 'current':
        return theme.colors.primary + '40';
      default:
        return theme.colors.border.primary;
    }
  }};
`;

const TimelineStepLabel = styled.div`
  font-weight: 600;
  color: ${({ theme, state }) => {
    switch (state) {
      case 'completed':
        return theme.colors.success;
      case 'current':
        return theme.colors.primary;
      case 'upcoming':
        return theme.colors.text.secondary;
      default:
        return theme.colors.text.primary;
    }
  }};
`;

const ErrorBanner = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 10px;
  background: ${props => props.theme.colors.error}10;
  border: 1px solid ${props => props.theme.colors.error}50;
  color: ${props => props.theme.colors.error};
  margin-bottom: 1.5rem;
  align-items: center;
`;

const ApplicationStatusPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoLookupTriggered = useRef(false);
  const [registrationNo, setRegistrationNo] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showLookupForm, setShowLookupForm] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupResult, setLookupResult] = useState(null);

  useEffect(() => {
    const initialRegNo = (searchParams.get('registration_no') || '').toUpperCase();
    const initialPhone = searchParams.get('phone') || '';
    if (!autoLookupTriggered.current && initialRegNo && initialPhone) {
      autoLookupTriggered.current = true;
      setRegistrationNo(initialRegNo);
      setPhone(initialPhone);
    }
  }, [searchParams]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setResult(null);

    if (!registrationNo.trim() || !phone.trim()) {
      setError('Both registration number and phone number are required.');
      return;
    }

    try {
      setLoading(true);
      const response = await membershipApplicationAPI.checkStatus({
        registration_no: registrationNo.trim(),
        phone: phone.trim(),
      });
      const data = response.data?.data || response.data;
      setResult(data);
      toast.success('Application status retrieved successfully');
    } catch (err) {
      console.error('Status lookup failed:', err);
      const message = err.response?.data?.message || 'Unable to find application. Please check the details and try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleLookupRegistration = async (event) => {
    event.preventDefault();
    setLookupResult(null);

    if (!lookupEmail.trim()) {
      toast.error('Email address is required to retrieve your registration ID.');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(lookupEmail.trim())) {
      toast.error('Please enter a valid email address.');
      return;
    }

    try {
      setLookupLoading(true);
      const response = await membershipApplicationAPI.findRegistration({
        email: lookupEmail.trim().toLowerCase(),
      });
      const message = response.data?.message || 'If an account exists with this email address, your registration number has been sent to your email.';
      setLookupResult({ email: lookupEmail.trim(), message });
      toast.success(message);
    } catch (err) {
      console.error('Registration lookup failed:', err);
      const message = err.response?.data?.message || 'Unable to find a registration with that email address.';
      toast.error(message);
      setLookupResult(null);
    } finally {
      setLookupLoading(false);
    }
  };

  return (
    <Container theme={theme}>
      <PublicHeader theme={theme} />
      <Content>
        <StatusCard theme={theme}>
          <Title theme={theme}>
            Check Application Status
            <HelpTooltip>
              <p>Use this page to see where an application is in the review process and access registration.</p>
              <ul>
                <li>Enter the <strong>tracking number</strong> and the <strong>phone number</strong> used on the application.</li>
                <li>If the status is <strong>Approved</strong>, you can click the button to complete membership registration.</li>
                <li>If the status is <strong>Needs Resubmission</strong>, read the remarks and update the registration form.</li>
              </ul>
            </HelpTooltip>
          </Title>
          <Description theme={theme}>
            Enter your registration number and the phone number used during submission to track your application progress.
          </Description>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <Input
              label="Registration Number"
              value={registrationNo}
              onChange={(e) => setRegistrationNo(e.target.value.toUpperCase())}
              placeholder="EWHRD-2025-00001"
              required
            />
            <Input
              label="Phone Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="09XXXXXXXX"
              required
            />
            <Button type="submit" variant="primary" leftIcon={<Search size={18} />} disabled={loading}>
              {loading ? 'Checking...' : 'Check Status'}
            </Button>
          </form>

          {error && (
            <ErrorBanner theme={theme} role="alert">
              <AlertCircle size={20} />
              <span>{error}</span>
            </ErrorBanner>
          )}

          {result && (
            <StatusSummary theme={theme}>
              <SummaryRow>
                <SummaryLabel theme={theme}>Applicant Name</SummaryLabel>
                <SummaryValue theme={theme}>{result.applicant_name}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel theme={theme}>Registration Number</SummaryLabel>
                <SummaryValue theme={theme}>{result.registration_no}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel theme={theme}>Current Status</SummaryLabel>
                <SummaryValue theme={theme}>{result.status_label}</SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryLabel theme={theme}>Submitted On</SummaryLabel>
                <SummaryValue theme={theme}>
                  {result.submitted_at ? new Date(result.submitted_at).toLocaleString() : 'Not available'}
                </SummaryValue>
              </SummaryRow>
              {result.remarks && (
                <SummaryRow>
                  <SummaryLabel theme={theme}>Remarks</SummaryLabel>
                  <SummaryValue theme={theme}>{result.remarks}</SummaryValue>
                </SummaryRow>
              )}

              {result.status === 'approved' && (
                <div style={{ 
                  marginTop: '1.5rem', 
                  padding: '1.5rem', 
                  background: theme.colors.success + '15', 
                  borderRadius: '12px',
                  border: `2px solid ${theme.colors.success}40`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <CheckCircle size={24} color={theme.colors.success} />
                    <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: theme.colors.success }}>
                      Congratulations! You are now a member!
                    </span>
                  </div>
                  <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
                    Your membership has been approved. A confirmation email with your Member ID has been sent to your email address.
                  </p>
                </div>
              )}

              {Array.isArray(result.timeline) && result.timeline.length > 0 && (
                <Timeline>
                  {result.timeline.map(step => (
                    <TimelineStep key={step.key} theme={theme} state={step.state}>
                      {step.state === 'completed' ? (
                        <CheckCircle size={20} color={theme.colors.success} />
                      ) : step.state === 'current' ? (
                        <AlertCircle size={20} color={theme.colors.primary} />
                      ) : (
                        <AlertCircle size={20} color={theme.colors.text.secondary} />
                      )}
                      <TimelineStepLabel theme={theme} state={step.state}>{step.label}</TimelineStepLabel>
                    </TimelineStep>
                  ))}
                </Timeline>
              )}
            </StatusSummary>
          )}

          <div style={{ marginTop: '3rem' }}>
            <button
              type="button"
              onClick={() => {
                setShowLookupForm(prev => !prev);
                setLookupResult(null);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.colors.primary,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <PhoneCall size={18} />
              {showLookupForm ? 'Hide "Forgot registration ID?"' : 'Forgot your registration ID?'}
            </button>

            {showLookupForm && (
              <Card theme={theme} style={{ marginTop: '1rem', padding: '1.5rem' }}>
                <h3 style={{ marginTop: 0, marginBottom: '0.75rem', color: theme.colors.text.primary }}>
                  Retrieve Your Registration ID
                </h3>
                <p style={{ color: theme.colors.text.secondary, marginTop: 0, marginBottom: '1.25rem' }}>
                  Enter the email address used during your application. We'll send your registration number to that email address.
                </p>
                <form onSubmit={handleLookupRegistration} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <Input
                    label="Email Address"
                    type="email"
                    value={lookupEmail}
                    onChange={(e) => setLookupEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    required
                  />
                  <Button type="submit" variant="warning" disabled={lookupLoading}>
                    {lookupLoading ? 'Sending...' : 'Send Registration ID to Email'}
                  </Button>
                </form>

                {lookupResult && (
                  <StatusSummary theme={theme} style={{ marginTop: '1.5rem', backgroundColor: theme.colors.success + '15', borderColor: theme.colors.success + '40' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                      <CheckCircle size={20} color={theme.colors.success} />
                      <SummaryValue theme={theme} style={{ color: theme.colors.success }}>
                        Email Sent Successfully
                      </SummaryValue>
                    </div>
                    <SummaryRow>
                      <SummaryLabel theme={theme}>Email Address</SummaryLabel>
                      <SummaryValue theme={theme}>{lookupResult.email}</SummaryValue>
                    </SummaryRow>
                    <p style={{ color: theme.colors.text.secondary, fontSize: '0.9rem', marginTop: '1rem', marginBottom: 0 }}>
                      {lookupResult.message || 'Your registration number has been sent to your email address. Please check your inbox (and spam folder).'}
                    </p>
                  </StatusSummary>
                )}
              </Card>
            )}
          </div>
        </StatusCard>
      </Content>
      <PublicFooter theme={theme} />
    </Container>
  );
};

export default ApplicationStatusPage;

